import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  ChevronDown,
  Maximize2,
  MessageSquare,
  Mic,
  MicOff,
  Minimize2,
  Monitor,
  MonitorOff,
  Phone,
  PhoneOff,
  Video,
  VideoOff,
} from 'lucide-react';
import { cameraOffByDefault } from '@bits/domain';
import type { ChatIceServersResponse, ChatSignalType } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errors';
import { useChat, type CallState } from '@/lib/chat-provider';
import { callEndedLabel, formatDuration, joinedPeerIds } from '@/lib/chat-model';
import { CallMesh } from '@/lib/webrtc';
import { SfuCall } from '@/lib/sfu';
import { Avatar } from '@/components/ui/avatar';

const ENDED_TOAST_MS = 4_000;

type Layout = 'compact' | 'full' | 'pill';

export function CallOverlay(): React.ReactElement | null {
  const { call } = useChat();
  if (!call) return null;
  if (call.phase === 'incoming') return <IncomingPanel key={call.call.id} state={call} />;
  if (call.phase === 'ended') return <EndedToast key={call.call.id} state={call} />;
  return <ActiveCall key={call.call.id} state={call} />;
}

// A ringtone needs a prior user gesture on the page; when the browser refuses,
// the panel and the title flash still make the call noticeable.
function useRingtone(active: boolean): void {
  React.useEffect(() => {
    if (!active) return;
    let context: AudioContext | null = null;
    try {
      context = new AudioContext();
    } catch {
      return;
    }
    const ctx = context;
    const beep = (at: number, hz: number): void => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = hz;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.2, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.4);
    };
    const ring = (): void => {
      void ctx.resume().catch(() => undefined);
      const t = ctx.currentTime;
      beep(t, 440);
      beep(t + 0.5, 480);
    };
    ring();
    const timer = window.setInterval(ring, 2_000);
    return () => {
      window.clearInterval(timer);
      void ctx.close().catch(() => undefined);
    };
  }, [active]);
}

function useTitleFlash(active: boolean, text: string): void {
  React.useEffect(() => {
    if (!active) return;
    const original = document.title;
    let on = false;
    const timer = window.setInterval(() => {
      on = !on;
      document.title = on ? text : original;
    }, 1_000);
    return () => {
      window.clearInterval(timer);
      document.title = original;
    };
  }, [active, text]);
}

// The call surface is the darkest token the palette has, so video reads
// without a new colour; controls sit on translucent white on top of it.
const SURFACE = 'bg-[hsl(var(--foreground))] text-[hsl(var(--background))]';

function RoundButton({
  label,
  active = false,
  danger = false,
  onClick,
  disabled,
  children,
}: {
  label: string;
  active?: boolean;
  danger?: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={danger ? undefined : active}
      className="group flex flex-col items-center gap-1.5 disabled:opacity-40"
    >
      <span
        className={cn(
          'flex size-12 items-center justify-center rounded-full transition-colors [&_svg]:size-5',
          danger
            ? 'bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))] group-hover:bg-[hsl(var(--destructive))]/85'
            : active
              ? 'bg-[hsl(var(--background))] text-[hsl(var(--foreground))]'
              : 'bg-white/12 group-hover:bg-white/22',
        )}
      >
        {children}
      </span>
      <span className="text-[11px] font-medium opacity-80">{label}</span>
    </button>
  );
}

function IncomingPanel({ state }: { state: CallState }): React.ReactElement {
  const { answerCall, declineCall } = useChat();
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const from = state.from?.name ?? 'Someone';
  const kind = state.call.kind === 'video' ? 'Video' : 'Audio';
  const group = state.call.participants.length > 2;
  useRingtone(true);
  useTitleFlash(true, `${kind} call from ${from}`);

  return (
    <div
      role="dialog"
      aria-label={`Incoming ${state.call.kind} call`}
      className={cn(
        'fixed bottom-4 right-4 z-50 flex w-[calc(100vw-2rem)] max-w-sm flex-col items-center gap-5 rounded-3xl p-6 shadow-2xl shadow-black/40',
        SURFACE,
      )}
    >
      <div className="relative mt-2">
        <span className="absolute inset-0 animate-ping rounded-full ring-4 ring-[hsl(var(--brand))]/60" />
        <span className="absolute -inset-2 rounded-full ring-2 ring-[hsl(var(--brand))]/40" />
        <Avatar name={from} personId={state.from?.personId} size="2xl" className="relative" />
      </div>
      <div className="text-center">
        <p className="font-display text-xl font-semibold leading-tight">{from}</p>
        <p className="mt-1 text-sm opacity-70">
          Incoming {state.call.kind} call{group ? ' · group' : ''}
        </p>
      </div>
      {error ? <p className="text-sm text-[hsl(var(--destructive))]">{error}</p> : null}
      <div className="flex w-full items-start justify-center gap-10">
        <RoundButton label="Decline" danger onClick={() => void declineCall()} disabled={busy}>
          <PhoneOff />
        </RoundButton>
        <button
          type="button"
          disabled={busy}
          aria-label="Answer"
          onClick={() => {
            setBusy(true);
            answerCall().catch((err: unknown) => {
              setBusy(false);
              setError(errorMessage(err));
            });
          }}
          className="group flex flex-col items-center gap-1.5 disabled:opacity-40"
        >
          <span className="flex size-12 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] shadow-[0_0_0_6px_rgba(1,103,220,0.25)] transition-colors group-hover:bg-[hsl(var(--brand))]/85 [&_svg]:size-5">
            {state.call.kind === 'video' ? <Video /> : <Phone />}
          </span>
          <span className="text-[11px] font-medium opacity-80">Answer</span>
        </button>
      </div>
    </div>
  );
}

function EndedToast({ state }: { state: CallState }): React.ReactElement {
  const { dismissCall, meId } = useChat();
  React.useEffect(() => {
    const timer = window.setTimeout(dismissCall, ENDED_TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [dismissCall]);
  return (
    <div
      role="status"
      className={cn(
        'fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-2xl px-4 py-3 text-sm shadow-xl shadow-black/30',
        SURFACE,
      )}
    >
      <PhoneOff className="size-4 opacity-70" />
      <span>{callEndedLabel(state.call, meId)}</span>
      <button type="button" onClick={dismissCall} className="ml-2 text-xs font-medium underline-offset-2 hover:underline">
        Dismiss
      </button>
    </div>
  );
}

function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

function useElapsed(since: string | null): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!since) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [since]);
  return since ? (now - Date.parse(since)) / 1000 : 0;
}

function ActiveCall({ state }: { state: CallState }): React.ReactElement {
  const { call, phase } = state;
  const { meId, hangUp, onSignal } = useChat();
  const [layout, setLayout] = React.useState<Layout>('compact');
  const [muted, setMuted] = React.useState(false);
  // Joining a big call starts with the camera off: the mesh sends one copy of
  // it to everyone, and the person can turn it on once they are in.
  const [cameraOff, setCameraOff] = React.useState(
    () => call.kind === 'video' && cameraOffByDefault(joinedPeerIds(call, meId).length),
  );
  const [local, setLocal] = React.useState<MediaStream | null>(null);
  const [remote, setRemote] = React.useState<ReadonlyMap<string, MediaStream>>(new Map());
  const [remoteVideo, setRemoteVideo] = React.useState<ReadonlyMap<string, boolean>>(new Map());
  const [peerScreen, setPeerScreen] = React.useState<ReadonlyMap<string, boolean>>(new Map());
  const [peerState, setPeerState] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [screen, setScreen] = React.useState<MediaStream | null>(null);
  const [relay, setRelay] = React.useState<boolean | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [shareError, setShareError] = React.useState<string | null>(null);
  const [ready, setReady] = React.useState(false);
  const meshRef = React.useRef<CallMesh | null>(null);
  const sfuRef = React.useRef<SfuCall | null>(null);
  const latestCall = React.useRef(call);
  latestCall.current = call;
  const screenRef = React.useRef<MediaStream | null>(null);
  const pendingSignals = React.useRef<Array<{ from: string; type: ChatSignalType; payload: unknown }>>([]);
  const elapsed = useElapsed(call.answeredAt);
  const peers = joinedPeerIds(call, meId);
  const isVideo = call.kind === 'video';
  const canShare =
    typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';

  const setFlag =
    (update: React.Dispatch<React.SetStateAction<ReadonlyMap<string, boolean>>>) =>
    (peer: string, value: boolean): void => {
      update((current) => {
        if ((current.get(peer) ?? false) === value) return current;
        const next = new Map(current);
        if (value) next.set(peer, true);
        else next.delete(peer);
        return next;
      });
    };

  const stopShare = React.useCallback(async (): Promise<void> => {
    const current = screenRef.current;
    screenRef.current = null;
    setScreen(null);
    current?.getTracks().forEach((t) => t.stop());
    await (meshRef.current ?? sfuRef.current)?.setScreenTrack(null);
  }, []);

  const startShare = async (): Promise<void> => {
    setShareError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    } catch {
      setShareError('Screen sharing was cancelled or refused.');
      return;
    }
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    screenRef.current = stream;
    setScreen(stream);
    // The browser's own "Stop sharing" bar ends the track; follow it.
    track.onended = () => void stopShare();
    await (meshRef.current ?? sfuRef.current)?.setScreenTrack(track);
  };
  useTitleFlash(phase === 'outgoing' && call.status === 'ringing', 'Calling…');

  React.useEffect(() => {
    if (!meId) return;
    let cancelled = false;
    let stream: MediaStream | null = null;
    let mesh: CallMesh | null = null;
    let sfu: SfuCall | null = null;
    const callId = call.id;
    const transport = call.transport;
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: isVideo });
      } catch {
        setError(
          isVideo
            ? 'Camera or microphone access was refused. Allow it in the browser and try again.'
            : 'Microphone access was refused. Allow it in the browser and try again.',
        );
        return;
      }
      if (cancelled) {
        stopStream(stream);
        return;
      }
      setLocal(stream);
      let ice: ChatIceServersResponse;
      try {
        ice = await api.chatIceServers();
      } catch {
        ice = { iceServers: [], relay: false, expiresAt: null };
      }
      if (cancelled) {
        stopStream(stream);
        return;
      }
      setRelay(ice.relay);
      const shared = {
        meId,
        localStream: stream,
        iceServers: ice.iceServers,
        onRemoteStream: (peer: string, remoteStream: MediaStream | null) => {
          setRemote((current) => {
            const next = new Map(current);
            if (remoteStream) next.set(peer, remoteStream);
            else next.delete(peer);
            return next;
          });
        },
        onRemoteVideo: setFlag(setRemoteVideo),
        onPeerScreen: setFlag(setPeerScreen),
        onPeerState: (peer: string, connectionState: string) => {
          setPeerState((current) => new Map(current).set(peer, connectionState));
        },
      };
      if (transport === 'sfu') {
        // One connection to the SFU; the camera follows the cameraOff effect
        // once ready, so a big call starts without it.
        sfu = new SfuCall({
          ...shared,
          api: {
            session: () => api.chatSfuSession(callId),
            publish: (body) => api.chatSfuPublish(callId, body),
            subscribe: (body) => api.chatSfuSubscribe(callId, body),
            renegotiate: (body) => api.chatSfuRenegotiate(callId, body),
            unpublish: (body) => api.chatSfuUnpublish(callId, body),
            unsubscribe: (body) => api.chatSfuUnsubscribe(callId, body),
          },
          onError: (err) => setShareError(errorMessage(err)),
        });
        sfuRef.current = sfu;
        await sfu.start();
        if (!cancelled) setReady(true);
        return;
      }
      mesh = new CallMesh({
        ...shared,
        send: (to, type, payload) => {
          void api.chatSignal(callId, { to, type, payload }).catch(() => undefined);
        },
      });
      meshRef.current = mesh;
      const queued = pendingSignals.current;
      pendingSignals.current = [];
      for (const s of queued) await mesh.handleSignal(s.from, s.type, s.payload);
      setReady(true);
    })();
    return () => {
      cancelled = true;
      mesh?.close();
      meshRef.current = null;
      sfu?.close();
      sfuRef.current = null;
      stopStream(stream);
      stopStream(screenRef.current);
      screenRef.current = null;
    };
    // setFlag is a stable factory over setState; the transport is rebuilt per call.
  }, [call.id, call.transport, isVideo, meId]);

  const peerKey = peers.join(',');
  // On the SFU what matters is who publishes what, not only who is in.
  const sessionKey =
    call.transport === 'sfu'
      ? JSON.stringify(call.participants.map((p) => [p.personId, p.status, p.session]))
      : '';
  React.useEffect(() => {
    if (!ready) return;
    if (meshRef.current) void meshRef.current.setPeers(peerKey ? peerKey.split(',') : []);
    if (sfuRef.current) void sfuRef.current.sync(latestCall.current);
  }, [ready, peerKey, sessionKey]);

  React.useEffect(() => {
    if (call.transport !== 'mesh') return;
    return onSignal((event) => {
      if (event.callId !== call.id) return;
      const mesh = meshRef.current;
      if (mesh) void mesh.handleSignal(event.from, event.type, event.payload);
      else pendingSignals.current.push({ from: event.from, type: event.type, payload: event.payload });
    });
  }, [call.id, call.transport, onSignal]);

  React.useEffect(() => {
    local?.getAudioTracks().forEach((t) => {
      t.enabled = !muted;
    });
  }, [local, muted]);
  React.useEffect(() => {
    local?.getVideoTracks().forEach((t) => {
      t.enabled = !cameraOff;
    });
    // On the SFU a camera that is off is not sent at all.
    if (ready && sfuRef.current) {
      void sfuRef.current.setCameraTrack(cameraOff ? null : (local?.getVideoTracks()[0] ?? null));
    }
  }, [local, cameraOff, ready]);

  React.useEffect(() => {
    if (layout !== 'full') return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setLayout('compact');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [layout]);

  const group = call.participants.length > 2;
  const other = call.participants.find((p) => p.personId !== meId);
  const title = group ? 'Group call' : (other?.name ?? 'Call');
  const status =
    phase === 'outgoing' && call.status === 'ringing'
      ? 'Calling…'
      : call.answeredAt
        ? formatDuration(elapsed)
        : 'Connecting…';
  const full = layout === 'full';

  if (layout === 'pill') {
    return (
      <button
        type="button"
        onClick={() => setLayout('compact')}
        aria-label="Expand call"
        className={cn(
          'fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-full py-2 pl-2 pr-4 text-sm shadow-xl shadow-black/30',
          SURFACE,
        )}
      >
        <span className="flex size-8 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]">
          {isVideo ? <Video className="size-4" /> : <Phone className="size-4" />}
        </span>
        <span className="font-medium">{title}</span>
        <span className="opacity-70">{status}</span>
        {muted ? <MicOff className="size-4 opacity-70" /> : null}
        <Maximize2 className="size-4 opacity-70" />
      </button>
    );
  }

  const pinned = peers.find((id) => peerScreen.get(id)) ?? null;
  const unpinned = pinned ? peers.filter((id) => id !== pinned) : peers;
  const columns =
    unpinned.length <= 1 ? 'grid-cols-1' : unpinned.length <= 4 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3';
  const renderTile = (peerId: string, big: boolean): React.ReactElement => {
    const participant = call.participants.find((p) => p.personId === peerId);
    const stream = remote.get(peerId) ?? null;
    const connection = peerState.get(peerId);
    return (
      <Tile
        key={peerId}
        personId={peerId}
        name={participant?.name ?? ''}
        stream={stream}
        showVideo={remoteVideo.get(peerId) ?? false}
        screen={peerScreen.get(peerId) ?? false}
        full={full || big}
        hint={
          connection === 'failed'
            ? 'Connection failed'
            : !stream || connection === 'connecting' || connection === 'new'
              ? 'Connecting…'
              : null
        }
      />
    );
  };

  return (
    <div
      role="dialog"
      aria-label={`${call.kind} call`}
      className={cn(
        'z-50 flex flex-col shadow-2xl shadow-black/40',
        SURFACE,
        full
          ? 'fixed inset-0 sm:inset-4 sm:rounded-3xl'
          : 'fixed inset-x-4 bottom-4 max-h-[80dvh] rounded-3xl sm:left-auto sm:right-4 sm:w-[34rem]',
      )}
    >
      <header className="flex items-center gap-3 px-5 pt-4 pb-2">
        {!group ? (
          <Avatar name={title} personId={other?.personId} size="md" />
        ) : (
          <span className="flex size-9 items-center justify-center rounded-full bg-white/12">
            <Phone className="size-4" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-lg font-semibold leading-tight">{title}</p>
          <p className="text-sm opacity-70">
            {status}
            {group ? ` · ${peers.length + 1} in call` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setLayout(full ? 'compact' : 'full')}
          aria-label={full ? 'Exit full screen' : 'Full screen'}
          className="flex size-9 items-center justify-center rounded-full hover:bg-white/12"
        >
          {full ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
        </button>
        <button
          type="button"
          onClick={() => setLayout('pill')}
          aria-label="Minimise call"
          className="flex size-9 items-center justify-center rounded-full hover:bg-white/12"
        >
          <ChevronDown className="size-5" />
        </button>
      </header>

      <div className={cn('relative min-h-0 flex-1 px-4', full ? 'py-2' : 'pb-2')}>
        {error ? (
          <p className="rounded-2xl bg-white/8 p-4 text-sm">{error}</p>
        ) : peers.length === 0 ? (
          <div className="flex h-full min-h-40 flex-col items-center justify-center gap-3 rounded-2xl bg-white/6 py-8 text-center">
            <Avatar name={title} personId={other?.personId} size="xl" />
            <p className="text-sm opacity-70">
              {phase === 'outgoing' && call.status === 'ringing' ? 'Ringing…' : 'Waiting for someone to join…'}
            </p>
          </div>
        ) : (
          pinned ? (
            <div className="flex h-full flex-col gap-2 overflow-y-auto">
              <div className="min-h-0 flex-1">{renderTile(pinned, true)}</div>
              {unpinned.length > 0 ? (
                <div className="flex shrink-0 gap-2 overflow-x-auto">
                  {unpinned.map((peerId) => (
                    <div key={peerId} className="w-28 shrink-0 sm:w-36">
                      {renderTile(peerId, false)}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className={cn('grid h-full gap-2 overflow-y-auto', columns, full ? 'auto-rows-fr' : '')}>
              {peers.map((peerId) => renderTile(peerId, false))}
            </div>
          )
        )}

        {screen && !error ? (
          <div
            className={cn(
              'absolute overflow-hidden rounded-xl bg-black ring-2 ring-[hsl(var(--brand))]/70',
              full ? 'bottom-4 right-6 w-44 sm:w-56' : 'bottom-4 right-6 w-28',
              'aspect-video',
            )}
          >
            <video
              ref={(el) => {
                if (el && el.srcObject !== screen) el.srcObject = screen;
              }}
              autoPlay
              muted
              playsInline
              className="h-full w-full object-contain"
            />
            <span className="absolute bottom-1 left-1 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white">
              Your screen
            </span>
          </div>
        ) : isVideo && local && !error ? (
          <div
            className={cn(
              'absolute overflow-hidden rounded-xl bg-black ring-2 ring-white/25',
              full ? 'bottom-4 right-6 w-44 sm:w-56' : 'bottom-4 right-6 w-28',
              'aspect-video',
            )}
          >
            <video
              ref={(el) => {
                if (el && el.srcObject !== local) el.srcObject = local;
              }}
              autoPlay
              muted
              playsInline
              className={cn('h-full w-full object-cover [transform:scaleX(-1)]', cameraOff ? 'opacity-20' : '')}
            />
            {cameraOff ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <VideoOff className="size-5 opacity-80" />
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <footer className="flex flex-col items-center gap-3 px-5 pb-5 pt-3">
        <div className="flex items-start justify-center gap-6">
          <RoundButton label={muted ? 'Unmute' : 'Mute'} active={muted} onClick={() => setMuted((m) => !m)} disabled={!local}>
            {muted ? <MicOff /> : <Mic />}
          </RoundButton>
          {isVideo ? (
            <RoundButton
              label={cameraOff ? 'Camera on' : 'Camera off'}
              active={cameraOff}
              onClick={() => setCameraOff((c) => !c)}
              disabled={!local}
            >
              {cameraOff ? <VideoOff /> : <Video />}
            </RoundButton>
          ) : null}
          {canShare ? (
            <RoundButton
              label={screen ? 'Stop sharing' : 'Share screen'}
              active={screen !== null}
              onClick={() => void (screen ? stopShare() : startShare())}
              disabled={!ready || !!error}
            >
              {screen ? <MonitorOff /> : <Monitor />}
            </RoundButton>
          ) : null}
          <Link
            to="/chat/$conversationId"
            params={{ conversationId: call.conversationId }}
            onClick={() => {
              if (full) setLayout('compact');
            }}
            className="group flex flex-col items-center gap-1.5"
            aria-label="Open chat"
          >
            <span className="flex size-12 items-center justify-center rounded-full bg-white/12 transition-colors group-hover:bg-white/22 [&_svg]:size-5">
              <MessageSquare />
            </span>
            <span className="text-[11px] font-medium opacity-80">Chat</span>
          </Link>
          <RoundButton label={error ? 'Leave' : 'Hang up'} danger onClick={() => void hangUp()}>
            <PhoneOff />
          </RoundButton>
        </div>
        {shareError ? <p className="text-center text-[11px] text-[hsl(var(--destructive))]">{shareError}</p> : null}
        {relay === false ? (
          <p className="text-center text-[11px] opacity-60">
            Relay not configured: calls across different networks may not connect.
          </p>
        ) : null}
      </footer>
    </div>
  );
}

// Whether video shows is decided by the remote track carrying frames, not by
// the call kind: an audio call carries a muted video line until a screen
// arrives on it, and a shared screen is shown whole rather than cropped.
function Tile({
  personId,
  name,
  stream,
  showVideo,
  screen,
  full,
  hint,
}: {
  personId: string;
  name: string;
  stream: MediaStream | null;
  showVideo: boolean;
  screen: boolean;
  full: boolean;
  hint: string | null;
}): React.ReactElement {
  const hasVideo = showVideo && stream !== null;
  return (
    <div
      className={cn(
        'relative flex h-full items-center justify-center overflow-hidden rounded-2xl bg-white/6',
        full ? 'min-h-40' : 'aspect-[4/3]',
      )}
    >
      {stream ? (
        <video
          ref={(el) => {
            if (el && el.srcObject !== stream) el.srcObject = stream;
          }}
          autoPlay
          playsInline
          className={cn('h-full w-full', screen ? 'object-contain' : 'object-cover', hasVideo ? '' : 'hidden')}
        />
      ) : null}
      {!hasVideo ? <Avatar name={name} personId={personId} size={full ? '2xl' : 'xl'} /> : null}
      <span className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white">
        {screen ? <Monitor className="size-3" aria-hidden="true" /> : null}
        {name}
        {screen ? <span className="opacity-70">· Sharing screen</span> : null}
        {hint ? <span className="opacity-70">· {hint}</span> : null}
      </span>
    </div>
  );
}
